import { request } from './request';

// 购物车数据类型定义
export interface CartItem {
  id: number;
  userId: number;
  productId?: number;
  code: string;
  quantity: number;
  size?: string;
  color?: string;
  customImage?: string;
  designData?: any;
  specialRequirements?: string;
  createdAt: string;
  updatedAt: string;
  product: {
    id: number;
    name: string;
    description: string;
    image: string;
    basePrice: number;
    customizable: boolean;
  };
}

// 本地缓存键
const CART_CACHE_KEY = 'cartItems';
const CART_LAST_SYNC_KEY = 'cartLastSync';

// 购物车状态管理
let cartState: CartItem[] = [];
let cartStateListeners: (() => void)[] = [];
let isInitialized = false;

// 从本地缓存加载购物车数据
const loadCartFromCache = (): CartItem[] => {
  try {
    const cached = localStorage.getItem(CART_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (error) {
    console.error('从本地缓存加载购物车失败:', error);
  }
  return [];
};

// 保存购物车数据到本地缓存
const saveCartToCache = (cartItems: CartItem[]): void => {
  try {
    localStorage.setItem(CART_CACHE_KEY, JSON.stringify(cartItems));
    localStorage.setItem(CART_LAST_SYNC_KEY, Date.now().toString());
  } catch (error) {
    console.error('保存购物车到本地缓存失败:', error);
  }
};

// 从API加载购物车数据
export const loadCartFromAPI = async (): Promise<CartItem[]> => {
  try {
    const response: any = await request({
      method: 'GET',
      url: 'cart'
    });
    
    if (response.success && response.result) {
      cartState = response.result;
      saveCartToCache(cartState);
      cartStateListeners.forEach(listener => listener());
      return response.result;
    }
    return [];
  } catch (error) {
    console.error('从API加载购物车失败:', error);
    // 如果API失败，尝试从本地缓存加载
    const cachedCart = loadCartFromCache();
    if (cachedCart.length > 0) {
      cartState = cachedCart;
      cartStateListeners.forEach(listener => listener());
    }
    return cachedCart;
  }
};

// 初始化购物车（用户登录后调用）
export const initializeCart = async (): Promise<void> => {
  if (isInitialized) return;
  
  try {
    // 首先从API加载最新数据
    await loadCartFromAPI();
    isInitialized = true;
  } catch (error) {
    console.error('初始化购物车失败:', error);
    // 如果API失败，从本地缓存加载
    const cachedCart = loadCartFromCache();
    cartState = cachedCart;
    cartStateListeners.forEach(listener => listener());
    isInitialized = true;
  }
};

// 添加商品到购物车（同时更新本地缓存和API）
export const addToCart = async (
  productId: number,
  code: string, 
  quantity: number, 
  size?: string, 
  color?: string,
  designData?: any,
  specialRequirements?: string
): Promise<boolean> => {
  try {
    console.log('addToCart params:', productId, code, quantity, size, color, designData, specialRequirements);
    const response: any = await request({
      method: 'POST',
      url: 'cart',
      data: {
        productId,
        code,
        quantity,
        size,
        color,
        designData,
        specialRequirements
      }
    });
    
    if (response.success) {
      // 更新本地状态
      if (response.result) {
        // 检查是否已存在相同商品
        const existingIndex = cartState.findIndex(item => 
          item.code === code && 
          item.size === (size || null) && 
          item.color === (color || null) 
        );
        
        if (existingIndex >= 0) {
          // 更新现有商品数量
          cartState[existingIndex].quantity += quantity;
        } else {
          // 添加新商品
          cartState.push(response.result);
        }
        
        // 保存到本地缓存并通知监听器
        saveCartToCache(cartState);
        cartStateListeners.forEach(listener => listener());
      }
      return true;
    }
    return false;
  } catch (error) {
    console.error('添加到购物车失败:', error);
    return false;
  }
};

// 从购物车删除商品（同时更新本地缓存和API）
export const removeFromCart = async (cartItemId: number): Promise<boolean> => {
  try {
    const response: any = await request({
      method: 'DELETE',
      url: `cart/${cartItemId}`
    });
    
    if (response.success) {
      // 从本地状态中移除
      cartState = cartState.filter(item => item.id !== cartItemId);
      
      // 保存到本地缓存并通知监听器
      saveCartToCache(cartState);
      cartStateListeners.forEach(listener => listener());
      return true;
    }
    return false;
  } catch (error) {
    console.error('从购物车删除失败:', error);
    return false;
  }
};

// 更新购物车商品数量（同时更新本地缓存和API）
export const updateCartItemQuantity = async (cartItemId: number, quantity: number): Promise<boolean> => {
  try {
    const response: any = await request({
      method: 'PUT',
      url: `cart/${cartItemId}`,
      data: { quantity }
    });
    
    if (response.success) {
      // 更新本地状态
      const itemIndex = cartState.findIndex(item => item.id === cartItemId);
      if (itemIndex >= 0) {
        cartState[itemIndex].quantity = quantity;
        
        // 保存到本地缓存并通知监听器
        saveCartToCache(cartState);
        cartStateListeners.forEach(listener => listener());
      }
      return true;
    }
    return false;
  } catch (error) {
    console.error('更新购物车商品数量失败:', error);
    return false;
  }
};

// 清空购物车（同时更新本地缓存和API）
export const clearCart = async (): Promise<boolean> => {
  try {
    const response: any = await request({
      method: 'DELETE',
      url: 'cart'
    });
    
    if (response.success) {
      // 清空本地状态
      cartState = [];
      
      // 保存到本地缓存并通知监听器
      saveCartToCache(cartState);
      cartStateListeners.forEach(listener => listener());
      return true;
    }
    return false;
  } catch (error) {
    console.error('清空购物车失败:', error);
    return false;
  }
};

// 获取购物车商品数量
export const getCartItemCount = (): number => {
  return cartState.reduce((total, item) => total + item.quantity, 0);
};

// 获取购物车总价
export const getCartTotal = (): number => {
  return cartState.reduce((total, item) => total + (item.product.basePrice * item.quantity), 0);
};

// 获取当前购物车状态
export const getCurrentCartState = (): CartItem[] => {
  return [...cartState];
};

// 订阅购物车状态变化
export const subscribeToCartState = (listener: () => void): (() => void) => {
  cartStateListeners.push(listener);
  return () => {
    cartStateListeners = cartStateListeners.filter(l => l !== listener);
  };
};

// 同步本地缓存到API（用于离线后重新上线时）
export const syncCartToAPI = async (): Promise<boolean> => {
  try {
    const lastSync = localStorage.getItem(CART_LAST_SYNC_KEY);
    const now = Date.now();
    
    // 如果超过5分钟没有同步，重新从API加载
    if (!lastSync || (now - parseInt(lastSync)) > 5 * 60 * 1000) {
      await loadCartFromAPI();
      return true;
    }
    
    return true;
  } catch (error) {
    console.error('同步购物车到API失败:', error);
    return false;
  }
};

// 重置购物车状态（用户登出时调用）
export const resetCart = (): void => {
  cartState = [];
  cartStateListeners = [];
  isInitialized = false;
  localStorage.removeItem(CART_CACHE_KEY);
  localStorage.removeItem(CART_LAST_SYNC_KEY);
};
